/** Player UI shared by Watch, Clip, Render and Choose: video + transport (every key has a button twin) + In/Out +
 * a visually hidden status region with the playhead for bots. */
import { useState, type FormEvent } from "react";
import { mediaUrl } from "../../api/client";
import { DownloadVideo } from "../../components/DownloadVideo";
import { fmtMs, nextOnGrid, timecode } from "../../lib/frames";
import { KeyButton, useKeyBindings, type KeyBinding } from "../keys";
import type { UrlPatch } from "../url";
import { RATES, type FrameController, type PlayerState } from "./controller";
import { SOUND_TOGGLE_TESTID, writeSoundPref } from "./sound";
import { usePlayerSelect, usePlayerState } from "./usePlayer";

export type Grids = { beat?: number[]; downbeat?: number[]; line?: number[]; section?: number[]; kick?: number[];
  snare?: number[] };
export type Describe = (tMs: number) => { section: string | null; line: string | null };
export type InOut = { inMs: number | null; outMs: number | null; loop: boolean; set: (p: UrlPatch) => void };

export function VideoSurface({ ctrl, videoRef, mediaId, webmId, label, vcodec, muted, poster }: {
  ctrl: FrameController;
  videoRef: (el: HTMLVideoElement | null) => void;
  mediaId: number | null | undefined;
  /** VP9/Opus twin of the same frames: offered first, for browsers without H.264 (e.g. Chromium-for-testing). */
  webmId?: number | null;
  label: string;
  vcodec?: string | null;
  muted?: boolean;
  poster?: string;
}) {
  const s = usePlayerState(ctrl);
  if (mediaId == null) {
    return <p className="ws8-muted" data-testid="player-no-media">{label}: no preview media yet.</p>;
  }
  return (
    <figure className="ws8-video">
      <video
        key={`${mediaId}:${webmId ?? ""}`}
        ref={videoRef}
        src={webmId == null ? mediaUrl(mediaId) : undefined}
        preload="auto"
        playsInline
        muted={muted}
        poster={poster}
        aria-label={label}
        data-testid="player-video"
        onClick={() => ctrl.togglePlay()}
      >
        {/* H.264 first (sharper preview); browsers without H.264 (agent Chromium) skip to the VP9 twin */}
        {webmId != null && <source src={mediaUrl(mediaId)} type='video/mp4; codecs="avc1.640028, mp4a.40.2"' />}
        {webmId != null && <source src={mediaUrl(webmId)} type="video/webm"
          onError={() => ctrl.sourcesFailed("MP4, WebM")} />}
      </video>
      {s.error && (
        <div role="alert" className="error-box" data-testid="player-error">
          <strong>This browser cannot play the preview.</strong> {s.error}.
          {vcodec ? ` Media codec: ${vcodec}.` : ""} Stepping still moves the logical playhead (time, frame, section,
          line), but no frame is verified. Use “Download video” to inspect it elsewhere.
        </div>
      )}
      <figcaption className="ws8-row">
        <span className="ws8-muted">{label}</span>
        <DownloadVideo mediaId={mediaId} />
      </figcaption>
    </figure>
  );
}

/** Visible timecode + hidden status region (current time ms, frame index, timecode, section, lyric line). */
export function PlayerStatus({ ctrl, describe }: { ctrl: FrameController; describe?: Describe }) {
  const s = usePlayerState(ctrl);
  const ctx = describe ? describe(s.tMs) : { section: null, line: null };
  const tc = timecode(s.tMs, s.fps);
  return (
    <>
      <p className="ws8-timecode" data-testid="timecode" aria-hidden="true">
        <span>{tc}</span> <span className="ws8-muted">{fmtMs(s.tMs)} · frame {s.frame} · {s.fps} fps</span>
      </p>
      <div
        className="ws8-sr-only"
        role="status"
        aria-live={s.playing ? "off" : "polite"}
        aria-atomic="true"
        aria-label="Player state"
        data-testid="player-state"
        data-time-ms={s.tMs}
        data-frame={s.frame}
        data-shown-frame={s.shownFrame ?? ""}
        data-verified={s.verified ? "1" : "0"}
        data-playing={s.playing ? "1" : "0"}
      >
        <span data-testid="ps-time-ms">Time {s.tMs} ms.</span>{" "}
        <span data-testid="ps-frame">Frame {s.frame}.</span>{" "}
        <span data-testid="ps-timecode">Timecode {tc}.</span>{" "}
        <span data-testid="ps-section">Section {ctx.section ?? "none"}.</span>{" "}
        <span data-testid="ps-line">Line {ctx.line ?? "none"}.</span>{" "}
        <span data-testid="ps-shown">{shownText(s)}</span>{" "}
        <span>{s.playing ? `Playing at ${s.rate}×.` : "Paused."}</span>
      </div>
    </>
  );
}

function shownText(s: PlayerState): string {
  if (s.error) return "Shown frame none (decode error).";
  if (s.seeking) return "Seeking.";
  if (s.shownFrame == null) return "Shown frame unknown.";
  return `Shown frame ${s.shownFrame} (${s.verified ? `verified by ${s.verifiedBy}` : "not the requested frame"}).`;
}

function gridBinding(ctrl: FrameController, grid: number[] | undefined, dir: 1 | -1): () => void {
  return () => {
    const t = nextOnGrid(ctrl.getState().tMs, grid ?? [], dir);
    if (t != null) ctrl.seekMs(t);
  };
}

/** Prev/next on the beat layers, each with a key twin (letters only: AltGr symbols do not reach the page on many
 * layouts): beat B N, bar (downbeat) G H, kick Z X, snare C V. */
const LAYER_NAV: { grid: "beat" | "downbeat" | "kick" | "snare"; name: string; prev: string; next: string }[] = [
  { grid: "beat", name: "beat", prev: "b", next: "n" },
  { grid: "downbeat", name: "downbeat", prev: "g", next: "h" },
  { grid: "kick", name: "kick", prev: "z", next: "x" },
  { grid: "snare", name: "snare", prev: "c", next: "v" },
];

/** Sound on / off (remembered: `vp.preview.sound`). When the browser refused autoplay with sound the player runs
 * muted and the button says so; a click brings the sound back. */
export function SoundToggle({ ctrl }: { ctrl: FrameController }) {
  const muted = usePlayerSelect(ctrl, (s) => s.muted);
  const blocked = usePlayerSelect(ctrl, (s) => s.soundBlocked);
  const label = !muted ? "Sound on" : blocked ? "Sound off — click for sound" : "Sound off";
  return (
    <button type="button" data-testid={SOUND_TOGGLE_TESTID} aria-pressed={!muted}
      data-blocked={blocked ? "1" : undefined}
      onClick={() => {
        const on = ctrl.getState().muted;
        ctrl.setMuted(!on);
        writeSoundPref(on);
      }}>
      {label}
    </button>
  );
}

/** Transport buttons + keyboard (J/K/L , . I O Space, beat-layer prev/next) with twins; `sound` adds the Sound
 * toggle (clip previews). */
export function Transport({ ctrl, grids, inOut, keys = true, rateParam, sound = false }: {
  ctrl: FrameController;
  grids?: Grids;
  inOut?: InOut;
  keys?: boolean;
  rateParam?: (rate: number) => void;
  sound?: boolean;
}) {
  const s = usePlayerState(ctrl);
  const setRate = (r: number) => {
    ctrl.setRate(r);
    rateParam?.(r);
  };
  const playKey: KeyBinding = { key: "Space", label: s.playing ? "Pause" : "Play", testId: "play-toggle",
    run: () => ctrl.togglePlay() };
  const bindings: KeyBinding[] = [
    playKey,
    { key: "j", label: "−1 s", testId: "back-1s", run: () => ctrl.stepMs(-1000) },
    { key: "k", label: "Pause", testId: "pause", run: () => ctrl.pause() },
    { key: "l", label: s.playing ? "Faster" : "Play forward", testId: "play-forward", run: () => {
      if (!ctrl.getState().playing) ctrl.play();
      else setRate(RATES.find((r) => r > ctrl.getState().rate) ?? 2);
    } },
    { key: ",", label: "−1 frame", testId: "frame-back", run: () => ctrl.stepFrames(-1) },
    { key: ".", label: "+1 frame", testId: "frame-forward", run: () => ctrl.stepFrames(1) },
  ];
  if (inOut) {
    bindings.push(
      { key: "i", label: "Set In", testId: "set-in", run: () => inOut.set({ in: ctrl.getState().tMs }) },
      { key: "o", label: "Set Out", testId: "set-out", run: () => inOut.set({ out: ctrl.getState().tMs }) },
    );
  }
  const layerNav: KeyBinding[] = grids ? LAYER_NAV.flatMap((n) => {
    const grid = grids[n.grid];
    const off = !grid || grid.length === 0;
    return [
      { key: n.prev, label: `Prev ${n.name}`, testId: `prev-${n.name}`, run: gridBinding(ctrl, grid, -1), disabled: off },
      { key: n.next, label: `Next ${n.name}`, testId: `next-${n.name}`, run: gridBinding(ctrl, grid, 1), disabled: off },
    ];
  }) : [];
  useKeyBindings([...bindings, ...layerNav], keys);
  const by = (id: string) => bindings.find((b) => b.testId === id) as KeyBinding;
  const nav: [string, string, () => void, number[] | undefined][] = grids ? [
    ["Prev line", "prev-line", gridBinding(ctrl, grids.line, -1), grids.line],
    ["Next line", "next-line", gridBinding(ctrl, grids.line, 1), grids.line],
    ["Prev section", "prev-section", gridBinding(ctrl, grids.section, -1), grids.section],
    ["Next section", "next-section", gridBinding(ctrl, grids.section, 1), grids.section],
  ] : [];
  return (
    <div className="ws8-transport" role="group" aria-label="Transport">
      <div className="ws8-row">
        <KeyButton b={playKey} className="ws8-primary" />
        <KeyButton b={by("pause")} />
        <KeyButton b={by("play-forward")} />
        <KeyButton b={by("back-1s")} />
        <KeyButton b={by("frame-back")} />
        <KeyButton b={by("frame-forward")} />
        <button type="button" data-testid="forward-1s" onClick={() => ctrl.stepMs(1000)}>+1 s</button>
        {sound && <SoundToggle ctrl={ctrl} />}
        <label className="ws8-inline">
          Speed{" "}
          <select data-testid="speed" value={String(s.rate)} onChange={(e) => setRate(Number(e.target.value))}>
            {RATES.map((r) => <option key={r} value={r}>{r}×</option>)}
          </select>
        </label>
      </div>
      {nav.length > 0 && (
        <div className="ws8-row" role="group" aria-label="Jump on the beat, line and section grids">
          {layerNav.map((b) => <KeyButton key={b.testId} b={b} />)}
          {nav.map(([label, id, run, grid]) => (
            <button key={id} type="button" data-testid={id} onClick={run} disabled={!grid || grid.length === 0}>
              {label}
            </button>
          ))}
        </div>
      )}
      {inOut && <InOutControls ctrl={ctrl} inOut={inOut} setIn={by("set-in")} setOut={by("set-out")} />}
      <GoTo ctrl={ctrl} />
    </div>
  );
}

function GoTo({ ctrl }: { ctrl: FrameController }) {
  const [v, setV] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const n = Number(v);
    if (v.trim() !== "" && Number.isFinite(n)) ctrl.seekMs(n);
  };
  return (
    <form className="ws8-row" onSubmit={submit} aria-label="Go to time">
      <label className="ws8-inline">
        Go to ms <input type="number" min={0} step={1} data-testid="goto-ms" value={v}
          onChange={(e) => setV(e.target.value)} />
      </label>
      <button type="submit" data-testid="goto-submit">Go</button>
    </form>
  );
}

function MsInput({ label, testId, value, onCommit }: {
  label: string; testId: string; value: number | null; onCommit: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value == null ? "" : String(value));
  const commit = () => {
    if (draft == null) return;
    const n = Number(draft);
    onCommit(draft.trim() === "" || !Number.isFinite(n) ? null : Math.max(0, Math.round(n)));
    setDraft(null);
  };
  return (
    <label className="ws8-inline">
      {label}{" "}
      <input type="number" min={0} step={1} data-testid={testId} value={shown}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }} />
    </label>
  );
}

function InOutControls({ ctrl, inOut, setIn, setOut }: {
  ctrl: FrameController; inOut: InOut; setIn: KeyBinding; setOut: KeyBinding;
}) {
  const { inMs, outMs, loop, set } = inOut;
  const dur = inMs != null && outMs != null ? outMs - inMs : null;
  return (
    <div className="ws8-row" role="group" aria-label="In and Out points">
      <KeyButton b={setIn} />
      <MsInput label="In (ms)" testId="in-ms" value={inMs} onCommit={(v) => set({ in: v })} />
      <button type="button" data-testid="goto-in" disabled={inMs == null}
        onClick={() => inMs != null && ctrl.seekMs(inMs)}>Go to In</button>
      <KeyButton b={setOut} />
      <MsInput label="Out (ms)" testId="out-ms" value={outMs} onCommit={(v) => set({ out: v })} />
      <button type="button" data-testid="goto-out" disabled={outMs == null}
        onClick={() => outMs != null && ctrl.seekMs(outMs)}>Go to Out</button>
      <button type="button" data-testid="loop-toggle" aria-pressed={loop} onClick={() => set({ loop: !loop })}>
        Loop In–Out
      </button>
      <button type="button" data-testid="clear-in-out" disabled={inMs == null && outMs == null}
        onClick={() => set({ in: null, out: null, loop: null })}>Clear In/Out</button>
      <span className="ws8-muted" data-testid="in-out-summary">
        {inMs != null ? `In ${fmtMs(inMs)}` : "In –"} · {outMs != null ? `Out ${fmtMs(outMs)}` : "Out –"}
        {dur != null ? ` · ${dur > 0 ? fmtMs(dur) : "Out before In"}` : ""}
      </span>
    </div>
  );
}
